import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, Lock, Mail, Pencil, Play, QrCode, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import EventForm from "@/components/attendance/EventForm";
import UsherPanel from "@/components/attendance/UsherPanel";
import UsherQrModal from "@/components/attendance/UsherQrModal";
import { AuditLog, CorrectionForm, LiveMatrix } from "@/components/attendance/LivePanels";
import { btnGhost, btnGold, downloadFile, errMsg, fmtDate, STATUS_STYLE } from "@/components/attendance/ui";

export default function AttendanceEvent() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [meta, setMeta] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [qrFor, setQrFor] = useState(null);

  const load = useCallback(() => api.get(`/attendance/events/${id}/live`).then((r) => setD(r.data))
    .catch((e) => { if (e?.response?.status === 404) nav("/attendance/dashboard"); }), [id, nav]);
  useEffect(() => { load(); api.get("/attendance/meta").then((r) => setMeta(r.data)); }, [load]);
  useEffect(() => {
    if (d?.event?.status !== "open") return;
    const t = setInterval(() => { if (!document.hidden) load(); }, 3000);
    return () => clearInterval(t);
  }, [d?.event?.status, load]);

  const ev = d?.event;
  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      if (ok) {
        const msg = typeof ok === "function" ? ok(r) : ok;
        (r?.data?.report_emailed === false ? toast.warning : toast.success)(msg);
      }
      await load(); return r;
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const close = () => window.confirm("Close counting? Ushers will no longer be able to count and the final report will be emailed.") &&
    run(() => api.post(`/attendance/events/${id}/close`), (r) => (r.data.report_emailed ? `Report emailed to ${r.data.emailed_to}` : `Event closed — report saved, but email failed: ${r.data.event.report_email_error || "unknown error"}`));
  const save = async (body) => { const r = await run(() => api.put(`/attendance/events/${id}`, body), "Event updated"); if (r) setEditing(false); };
  const remove = () => window.confirm("Delete this event?") && run(() => api.delete(`/attendance/events/${id}`)).then((r) => r && nav("/attendance/dashboard"));
  const download = () => downloadFile(api, `/attendance/events/${id}/report.xlsx`, `HOH-Attendance-${ev.name}-${ev.date}.xlsx`).catch(() => toast.error("Download failed"));

  if (!d || !meta) return <div className="py-32 flex justify-center"><Loader2 className="animate-spin text-[#D4AF37]" /></div>;
  return (
    <div className="max-w-[1240px] mx-auto px-4 sm:px-6 lg:px-10 py-8" data-testid="attendance-event">
      <Link to="/attendance/dashboard" className="text-xs text-neutral-400 hover:text-white inline-flex items-center gap-1"><ArrowLeft size={13} /> All events</Link>
      <div className="flex flex-wrap justify-between items-end gap-4 mt-4">
        <div>
          <div className="flex items-center gap-3">
            <span className="text-[10px] tracking-widest uppercase text-neutral-500">{ev.custom_type || ev.type}</span>
            <span data-testid="att-ev-status" className={`text-[9px] tracking-widest uppercase px-2 py-0.5 rounded border ${STATUS_STYLE[ev.status]}`}>{ev.status === "open" ? "● live" : ev.status}</span>
          </div>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-white mt-1">{ev.name}</h1>
          <div className="text-sm text-neutral-400 mt-1">{fmtDate(ev.date)} {ev.start_time} {ev.venue && `· ${ev.venue}`}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {ev.status !== "closed" && <button onClick={() => setQrFor("")} data-testid="att-show-qr" className={btnGhost}><QrCode size={13} /> Show Usher QR Code</button>}
          {ev.status !== "closed" && <button onClick={() => setEditing(true)} data-testid="att-ev-edit" className={btnGhost}><Pencil size={13} /> Edit</button>}
          {ev.status === "scheduled" && <button onClick={remove} data-testid="att-ev-delete" className={btnGhost}><Trash2 size={13} /></button>}
          {ev.status === "scheduled" && <button disabled={busy} onClick={() => run(() => api.post(`/attendance/events/${id}/open`), "Counting is open")} data-testid="att-ev-open" className={btnGold}><Play size={13} /> Open counting</button>}
          {ev.status === "open" && <button disabled={busy} onClick={close} data-testid="att-ev-close" className={btnGold}><Lock size={13} /> Close & send report</button>}
          <button onClick={download} data-testid="att-ev-download" className={btnGhost}><Download size={13} /> Excel</button>
          {ev.status === "closed" && <button disabled={busy} onClick={() => run(() => api.post(`/attendance/events/${id}/email-report`), (r) => (r.data.report_emailed ? `Report emailed to ${r.data.emailed_to}` : `Email failed: ${r.data.error || "unknown error"}`))} data-testid="att-ev-resend" className={btnGhost}><Mail size={13} /> Resend report</button>}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mt-8">
        <section className="lg:col-span-2 luxury-card rounded-xl p-5">
          <div className="flex justify-between items-baseline mb-4">
            <h2 className="text-xs tracking-widest uppercase text-neutral-400">Live totals</h2>
            <span className="font-serif-display text-5xl text-[#D4AF37]" data-testid="att-ev-total">{d.total}</span>
          </div>
          <LiveMatrix event={ev} matrix={d.matrix} byCategory={d.by_category} total={d.total} />
          {ev.report_email_status && <p className="text-xs text-neutral-500 mt-4" data-testid="att-ev-report-status">Report {ev.report_email_status === "sent" ? `emailed to ${ev.report_emailed_to}` : `email failed${ev.report_email_error ? ` (${ev.report_email_error})` : ""} — use Resend or download the Excel file`}.</p>}
        </section>
        <section className="luxury-card rounded-xl p-5">
          <h2 className="text-xs tracking-widest uppercase text-neutral-400 mb-4">Ushers</h2>
          <UsherPanel event={ev} ushers={d.ushers} onChange={load} disabled={ev.status === "closed"} onQr={setQrFor} />
        </section>
        <section className="lg:col-span-2 luxury-card rounded-xl p-5">
          <h2 className="text-xs tracking-widest uppercase text-neutral-400 mb-4">Audit & correction history</h2>
          <AuditLog event={ev} log={d.log} />
        </section>
        {ev.status === "open" && (
          <section className="luxury-card rounded-xl p-5">
            <h2 className="text-xs tracking-widest uppercase text-neutral-400 mb-4">Correct a count</h2>
            <CorrectionForm event={ev} onDone={load} />
          </section>
        )}
      </div>
      {editing && <EventForm initial={ev} types={meta.event_types} defaults={meta.default_categories} onSave={save} onClose={() => setEditing(false)} />}
      {qrFor !== null && <UsherQrModal event={ev} ushers={d.ushers} initialId={qrFor} onClose={() => setQrFor(null)} />}
    </div>
  );
}
